import type { GlobalEvent } from '@inertiajs/core';
import { router } from '@inertiajs/vue3';
import { useConfirmDialog, useEventListener } from '@vueuse/core';
import type { ComputedRef } from 'vue';
import { onBeforeUnmount, ref } from 'vue';

type UseUploadLeaveGuardOptions = {
    isActive: () => boolean;
    isSelfInitiated: () => boolean;
    markSelfInitiated: () => void;
    onLeave: () => void;
};

type UseUploadLeaveGuardReturn = {
    isOpen: ComputedRef<boolean>;
    onConfirm: () => void;
    onCancel: () => void;
    onUpdateOpen: (isOpen: boolean | undefined) => void;
};

/**
 * Guards navigation while an upload is in progress:
 *  - Intercepts Inertia visits with a branded confirmation reveal.
 *  - Warns on browser-initiated unloads via `beforeunload`.
 *  - Cancels the upload via `pagehide` once the user has confirmed.
 */
export function useUploadLeaveGuard(options: UseUploadLeaveGuardOptions): UseUploadLeaveGuardReturn {
    const { reveal, confirm, cancel, isRevealed } = useConfirmDialog<string>();
    const pendingLeaveHref = ref<string | null>(null);

    // Resolved lazily so `window` is only touched client-side. Vue SSR
    // (Inertia SSR) evaluates this composable inside setup() on Node, where
    // referencing `window` directly would throw.
    const getWindow = (): Window | null => (typeof window !== 'undefined' ? window : null);

    const removeBeforeVisit = router.on('before', (event: GlobalEvent<'before'>): boolean | void => {
        if (!options.isActive()) {
            return;
        }

        if (options.isSelfInitiated()) {
            return;
        }

        const targetHref = event.detail.visit.url.toString();

        event.preventDefault();
        pendingLeaveHref.value = targetHref;

        void reveal().then(({ isCanceled }) => {
            const href = pendingLeaveHref.value;
            pendingLeaveHref.value = null;

            if (isCanceled || !href) {
                return;
            }

            options.onLeave();
            options.markSelfInitiated();
            router.visit(href);
        });
    });

    useEventListener(getWindow, 'beforeunload', (event: BeforeUnloadEvent) => {
        if (!options.isActive() || options.isSelfInitiated()) {
            return;
        }

        event.preventDefault();
        event.returnValue = '';
    });

    useEventListener(getWindow, 'pagehide', () => {
        if (!options.isActive() || options.isSelfInitiated()) {
            return;
        }

        options.onLeave();
    });

    onBeforeUnmount(() => {
        removeBeforeVisit();
    });

    function onConfirm(): void {
        confirm();
    }

    function onCancel(): void {
        cancel();
    }

    function onUpdateOpen(nextOpen: boolean | undefined): void {
        if (nextOpen) {
            return;
        }

        if (!isRevealed.value) {
            return;
        }

        cancel();
    }

    return {
        isOpen: isRevealed,
        onConfirm,
        onCancel,
        onUpdateOpen,
    };
}
